import React, { PureComponent, Fragment } from 'react';
import { Grid, Input, Icon } from 'semantic-ui-react';
import classNames from 'classnames';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { get } from 'lodash';

import { NutritionalHoverConnected } from 'Common/Components/NutritionalHover/NutritionalHover';
import { RecipeCategoryEnum } from 'Common/Constants/Enumeration/RecipeCategoryEnum';
import {
  EDIT_MENU_TYPES,
  EDIT_MENU_QUERYSTRINGS,
} from 'Pages/managemenu/helpers/Constants';

import {
  groupItemsByCategory,
  getNumberOfWeeks,
  shouldAllowCombine,
  formatDayText,
  canEditAppliedMenu,
  getDaysToShow,
} from '../utilities';
import { ALACARTE_RECIPE_CATEGORY, VIEW_BY } from '../constants';
import { ViewPropTypes } from './ViewPropTypes';
import { DailyContributions } from './DailyContributions';

import 'Styles/pages/managemenu/weeklyView.less';

class WeeklyView extends PureComponent {
  static propTypes = ViewPropTypes;

  static defaultProps = {
    onDeleteClick: () => {},
    onBulkDeleteClick: () => {},
    onHideClick: () => {},
    onBulkHideClick: () => {},
    disableHover: true,
    onProjectedServingsChange: (draggableId) => () => {},
    onProjectedServingsBlur: (draggableId) => () => {},
    onQtyOfPortionChange: (draggableId) => () => {},
    onProjectedServingsPercentageChange: () => {},
    showDaysOfTheWeek: false,
    showMealContributionReq: false,
    openRecipe: () => {},
  };

  static qtyOfPortionPattern = /^(\d{1,2}[.]?|\d{1,2}[.]\d{1,4})?$/;

  isItemSelected = (id) => !!this.props.selectedRecipesById[id];
  isItemCopied = (id) => !!this.props.copiedRecipesById[id];
  isItemInactive = (inactive) => inactive === 'Y';

  renderWeekDayHeader = (day) => {
    const {
      analysisDays,
      weekNumber,
      showDaysOfTheWeek,
      queryParams,
      menuType,
      editableAppliedDays,
    } = this.props;

    const requirements = this.getDayRequirements(day);
    const dayRequirementsMet = this.checkIfDailyRequirementsMet(requirements);

    const formatArgs = {
      weekNumber,
      showDaysOfTheWeek,
      analysisDays,
      dayNumber: day,
    };

    if (menuType === EDIT_MENU_TYPES.APPLIED_MENU) {
      formatArgs.startDate = queryParams[EDIT_MENU_QUERYSTRINGS.START_DATE];
    }

    const canEdit = canEditAppliedMenu({
      menuType,
      editableAppliedDays,
      dayNumber: day,
    });

    return (
      <div
        className={classNames('week-button', {
          'requirements-met': canEdit && dayRequirementsMet,
          'requirements-not-met': canEdit && !dayRequirementsMet,
        })}
      >
        {formatDayText(formatArgs)}
      </div>
    );
  };

  renderSideWeekButtons(daysToShow) {
    const {
      analysisDays,
      currentWeek,
      onWeekChange,
      weeklyRequirements,
    } = this.props;

    const numberOfWeeks = getNumberOfWeeks(daysToShow.length, analysisDays);

    const buttons = [];

    for (let i = 0; i < numberOfWeeks; i++) {
      let className = classNames('side-button', {
        active: currentWeek - 1 === i,
      });
      const weekNumber = i + 1;

      if (Array.isArray(weeklyRequirements) && weeklyRequirements.length > 0) {
        const requirements = get(weeklyRequirements[i], 'List', []);
        const weeklyRequirementsMet = requirements.every(
          (req) => req.WeeklyMet
        );
        className = classNames(className, {
          'requirements-met': weeklyRequirementsMet,
          'requirements-not-met': !weeklyRequirementsMet,
        });
      } else {
        className = classNames(className, 'requirements-met');
      }

      buttons.push(
        <div
          className={className}
          key={weekNumber}
          onClick={onWeekChange(weekNumber)}
        >
          {weekNumber}
        </div>
      );
    }

    return buttons;
  }

  getCategoryTotalServings(categoryItems) {
    let total = 0;

    categoryItems.forEach((item) => {
      total += item.ProjectedServings || 0;
    });

    return total;
  }

  computeItemPercentage(item) {
    const { plannedServings } = this.props;

    return Number(
      ((item.ProjectedServings / plannedServings) * 100).toFixed(3)
    );
  }

  sumCategoryItemPercentage(items) {
    return Number(
      items
        .reduce((accumulator, currentItem) => {
          return this.computeItemPercentage(currentItem) + accumulator;
        }, 0)
        .toFixed(3)
    );
  }

  onDeleteClick = (recipe) => (e) => {
    const { onDeleteClick } = this.props;
    e.stopPropagation();

    onDeleteClick(recipe);
  };

  onBulkDeleteRecipesClick = (recipes) => (e) => {
    const { onBulkDeleteClick } = this.props;

    onBulkDeleteClick(recipes);
  };

  onBulkHideRecipesClick = (recipes) => (e) => {
    const { onBulkHideClick } = this.props;
    const allVisible = this.checkIfAllRecipesVisible(recipes);

    const items = recipes.map((recipe) => ({
      recipeDraggableId: recipe.draggableId,
      displayOnCalendar: !allVisible,
    }));

    onBulkHideClick(items);
  };

  onHideClick = (recipeDraggableId, displayOnCalendar) => (e) => {
    const { onHideClick } = this.props;
    e.stopPropagation();

    onHideClick(recipeDraggableId, !displayOnCalendar);
  };

  onItemClick = (id) => (e) => {
    const {
      selectedRecipesById,
      selectMenuItem,
      clearSelection,
      isReadOnly,
    } = this.props;

    if (isReadOnly) {
      return;
    }

    if (!Object.keys(selectedRecipesById).length) {
      selectMenuItem(id);
    }

    if (Object.keys(selectedRecipesById).length) {
      if (!e.metaKey && !e.ctrlKey) {
        clearSelection();
      }
      selectMenuItem(id);
    }

    if (!e.defaultPrevented && this.itemRefs[id]) {
      this.itemRefs[id].focus();
    }

    e.stopPropagation();
  };

  onItemDoubleClick = (recipeId) => (e) => {
    this.props.openRecipe(recipeId);
  };

  onInputClick = (e) => {
    // Prevent selecting item when input is focused
    e.stopPropagation();
  };

  onKeyDown = (e) => {
    const { key } = e;

    if (key === 'Delete' || key === 'Backspace') {
      // Prevent deleting the item if input is focused
      e.stopPropagation();
    }
  };

  onRemoveChild = (childId, parentId) => (e) => {
    const { removeChild } = this.props;
    removeChild(childId, parentId);
    e.stopPropagation();
  };

  itemRefs = {};

  Item = (props) => {
    const {
      isReadOnly,
      disableHover,
      onProjectedServingsChange,
      onProjectedServingsBlur,
      onProjectedServingsPercentageChange,
      onProjectedServingsPercentageBlur,
      item,
      combineTarget,
      isChild,
      parentId,
    } = props;
    const selected = this.isItemSelected(item.draggableId);
    const copied = this.isItemCopied(item.draggableId);
    return (
      <div
        tabIndex={item.OrderNum}
        ref={(ref) => (this.itemRefs[item.draggableId] = ref)}
        key={item.draggableId}
        onClick={this.onItemClick(item.draggableId)}
        className={classNames('daily-menu-row', {
          selected,
          copied,
          entree: item.RecipeCategoryId === RecipeCategoryEnum.Entree,
          combineTarget,
          child: isChild,
        })}
        data-draggable-id={`${item.draggableId}`}
      >
        <div className="daily-category-column">
          {isChild && (
            <Icon
              onClick={this.onRemoveChild(item.draggableId, parentId)}
              className="child-remove-icon"
              name="minus"
            />
          )}
          <NutritionalHoverConnected
            recipeId={item.RecipeId}
            showDisclaimer={false}
            showServingCost={true}
            disableHover={disableHover}
            openDelay={1000}
            openRecipe={this.props.openRecipe}
          >
            <div
              onDoubleClick={() => this.props.openRecipe(item.RecipeId)}
              className={classNames('daily-menu-text daily-menu-item-name', {
                inactive: this.isItemInactive(item.RecipeInactive),
              })}
            >
              {item.Name}
            </div>
          </NutritionalHoverConnected>
        </div>

        <div className="daily-menu-column planned-servings">
          <Input
            type="text"
            maxLength={6}
            fluid
            value={item.ProjectedServings}
            onChange={onProjectedServingsChange(item.draggableId)}
            onBlur={onProjectedServingsBlur(item.draggableId)}
            disabled={isReadOnly}
            onClick={this.onInputClick}
            onKeyDown={this.onKeyDown}
            className="menu-input"
          />
        </div>
        <div className="daily-menu-column servings-percentage">
          <Input
            value={item.ServingsPercentage}
            onChange={onProjectedServingsPercentageChange(item.draggableId)}
            onBlur={onProjectedServingsPercentageBlur(item.draggableId)}
            disabled={isReadOnly}
            onClick={this.onInputClick}
            onKeyDown={this.onKeyDown}
            maxLength={6}
            icon="percent"
            fluid
            className="menu-input"
          />
        </div>
      </div>
    );
  };

  renderCategoryItems(items, dragDisabled) {
    const { isReadOnly, lassoMode } = this.props;

    const { Item } = this;
    return items.map((item, i) => {
      return (
        <Draggable
          index={item.OrderNum}
          draggableId={item.draggableId}
          key={i}
          isDragDisabled={isReadOnly || dragDisabled || lassoMode}
        >
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
            >
              <Item
                {...this.props}
                item={item}
                combineTarget={Boolean(snapshot.combineTargetFor)}
              />
              {item.Children.map((child) => (
                <Item
                  key={child.draggableId}
                  {...this.props}
                  item={child}
                  isChild
                  parentId={item.draggableId}
                />
              ))}
            </div>
          )}
        </Draggable>
      );
    });
  }

  checkIfAllRecipesVisible(recipes) {
    return recipes.every((recipe) => recipe.DisplayOnCalendar);
  }

  checkIfDailyRequirementsMet(requirements) {
    return requirements.length === 0;
  }

  getDayRequirements(day) {
    const { unmetDailyRequirements } = this.props;

    return get(unmetDailyRequirements, `[${day - 1}].List`, []);
  }

  renderWeekDay(day) {
    const {
      menuDaysMap,
      recipesById,
      recipeCategories,
      plannedServings,
      showMealContributionReq,
      selectedDays,
      onDayClick,
      dailyContributions,
      draggingMeta,
      searchResults,
      searchDroppableId,
      isReadOnly,
      editableAppliedDays,
      menuType,
      copiedDays,
    } = this.props;

    if (!menuDaysMap[day]) {
      return null;
    }

    const itemsByCategory = groupItemsByCategory(
      menuDaysMap[day],
      recipesById,
      true
    );

    const requirements = this.getDayRequirements(day);

    const selected = selectedDays[day];

    let isCombineEnabled = false;
    const draggingId = get(draggingMeta, 'draggableId');

    if (draggingId) {
      let recipe = null;
      if (get(draggingMeta, 'source.droppableId') === searchDroppableId) {
        const index = get(draggingMeta, 'source.index');
        recipe = get(searchResults, index);
      } else {
        recipe = recipesById[draggingId];
      }
      isCombineEnabled = shouldAllowCombine(recipe);
    }
    const canEdit = canEditAppliedMenu({
      menuType,
      editableAppliedDays,
      dayNumber: day,
    });

    const copied = copiedDays[day];

    return (
      <Grid padded className="daily-menu-view">
        <Grid.Row>
          <Grid.Column width={16} className="daily-menu-section">
            <div
              className={classNames('daily-week-headers', {
                selected,
                cursorPointer: !isReadOnly && canEdit,
              })}
              onClick={(e) => {
                if (!isReadOnly && canEdit) {
                  onDayClick(day)(e);
                }
              }}
            >
              {this.renderWeekDayHeader(day)}
            </div>
            <div
              className={classNames('daily-menu-container', {
                selected,
                copied,
              })}
            >
              <Droppable
                droppableId={`${day}`}
                isCombineEnabled={isCombineEnabled}
                isDropDisabled={isReadOnly || !canEdit}
              >
                {(provided, snapshot) => {
                  return (
                    <Fragment>
                      <div className="daily-menu" ref={provided.innerRef}>
                        <div className="daily-menu-row daily-menu-header">
                          <div className="daily-category-column">Category</div>
                          <div className="daily-menu-column planned-servings">
                            Planned
                          </div>
                          <div className="daily-menu-column servings-percentage">
                            %
                          </div>
                        </div>
                        {/* Intentional div tag here so that its easier to select the last daily-menu-item-list */}
                        <div>
                          {recipeCategories.map((category, i) => {
                            const { Id, Description } = category;
                            const categoryItems = itemsByCategory[Id] || [];

                            const parentItems = categoryItems.filter(
                              (i) => i.level === 0
                            );

                            if (parentItems.length === 0) {
                              return null;
                            }

                            const firstItemInCategory = parentItems[0];

                            const totalServings = this.getCategoryTotalServings(
                              categoryItems
                            );
                            const totalPercentage = this.sumCategoryItemPercentage(
                              categoryItems
                            );
                            const percentageHeaderClass = classNames(
                              'daily-menu-column servings-percentage',
                              {
                                'percentage-met': totalPercentage === 100,
                                'percentage-not-met': totalPercentage !== 100,
                              }
                            );

                            return (
                              <div key={i} className="daily-menu-item-list">
                                <Draggable
                                  index={firstItemInCategory.OrderNum - 1}
                                  draggableId={`${day}-${category.Id}`}
                                  isDragDisabled={true}
                                >
                                  {(provided, snapshot) => {
                                    return (
                                      <div
                                        className="daily-menu-row daily-menu-category"
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                      >
                                        <div className="daily-category-column">
                                          {Description}
                                        </div>
                                        <div className="daily-menu-column planned-servings-header">
                                          {plannedServings}
                                        </div>
                                        <div className={percentageHeaderClass}>
                                          {this.sumCategoryItemPercentage(
                                            categoryItems
                                          )}
                                          % ({totalServings})
                                        </div>
                                      </div>
                                    );
                                  }}
                                </Draggable>

                                {this.renderCategoryItems(
                                  parentItems,
                                  Id === ALACARTE_RECIPE_CATEGORY.Id
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {provided.placeholder}
                        {canEdit &&
                          showMealContributionReq &&
                          requirements.map((item, i) => (
                            <div key={i} className="daily-menu-requirement">
                              {item}
                            </div>
                          ))}
                      </div>
                    </Fragment>
                  );
                }}
              </Droppable>
            </div>
            <div className="daily-contribtion-container">
              <DailyContributions
                contributions={dailyContributions[day - 1]}
                viewType={VIEW_BY.WEEK}
              />
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }

  render() {
    const {
      weekNumber,
      analysisDays,
      menuDaysMap,
      numberOfDays,
      queryParams,
      schoolCalendar,
    } = this.props;

    const daysToShow = getDaysToShow(
      numberOfDays,
      queryParams[EDIT_MENU_QUERYSTRINGS.START_DATE],
      analysisDays,
      schoolCalendar
    );

    const days = [];

    const endingDay = weekNumber * analysisDays;
    const endIndex =
      endingDay > daysToShow.length ? daysToShow.length : endingDay;
    const daysForGivenWeek = daysToShow.slice(
      (weekNumber - 1) * analysisDays,
      endIndex
    );

    daysForGivenWeek.forEach((day) => {
      days.push(day);
    });

    return (
      <div className="weekly-view">
        <div className="side-button-container">
          {this.renderSideWeekButtons(daysToShow)}
        </div>
        {days.map((day, i) => (
          <div
            className={classNames(
              'weekly-view-day',
              i === 0 && 'first',
              !menuDaysMap[day] && 'empty'
            )}
            key={day}
          >
            {this.renderWeekDay(day)}
          </div>
        ))}
      </div>
    );
  }
}

export { WeeklyView };



// WEBPACK FOOTER //
// ./src/pages/managemenu/subpages/editmenu/components/WeeklyView.js